/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { Divider, Form, Input, message, Modal, Radio, Select, Skeleton, Spin } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import BadRequestError from '~lib/badRequestError';
import {
  getAirlineCodes,
  getAirlineSystemModels,
  getSystemModelProfile,
  provisionSystem,
} from '~lib/netconnClient';
import constants from '~util/constants';
import handleAuthErr from '~util/handleAuthErr';
import logger from '~util/logger';
import normalizeAircards from '~util/normalizeAircards';
import AircardForm, { checkDuplicateAircardValues } from './components/AircardForm';

const ProvisionForm = Form.create({ name: 'provision' })(({ form, nai, cancel }) => {
  const [division, setDivision] = useState();
  const [airlineCodes, setAirlineCodes] = useState();
  const [selectedAirlineCode, setSelectedAirlineCode] = useState();
  const [systemModels, setSystemModels] = useState({});
  const [selectedSystemModel, setSelectedSystemModel] = useState();
  const [loading, setLoading] = useState({
    airlineCodes: false,
    systemModels: false,
    systemModelProfile: false,
  });

  const { getFieldDecorator, resetFields } = form;
  const { Option } = Select;

  useEffect(() => {
    const loadAirlineCodes = async () => {
      setLoading(prev => ({ ...prev, airlineCodes: true }));
      try {
        const data = await getAirlineCodes();
        if (!!data?.filter) {
          // We won't allow them to select the BA airline code as the dropdown only renders for the CA division
          setAirlineCodes(data.filter(airlineCode => airlineCode !== constants.BA).sort());
          setLoading(prev => ({ ...prev, airlineCodes: false }));
        }
      } catch (e) {
        handleAuthErr(err => {
          logger.error(err);
          message.error(constants.API_ERR_MESSAGE, 5);
          setLoading(prev => ({ ...prev, airlineCodes: false }));
          cancel();
        })(e);
      }
    };

    loadAirlineCodes();
  }, [cancel]);

  useEffect(() => {
    if (!!division) {
      if (division === constants.BA) {
        setSelectedAirlineCode(constants.BA);
      } else {
        setSelectedAirlineCode();
      }
    }
  }, [division]);

  useEffect(() => {
    const getSystemModels = async airlineCode => {
      setLoading(prev => ({ ...prev, systemModels: true }));
      try {
        const data = await getAirlineSystemModels(airlineCode);
        setSystemModels(data);
        setLoading(prev => ({ ...prev, systemModels: false }));
      } catch (e) {
        handleAuthErr(err => {
          logger.error(err);
          message.error(constants.API_ERR_MESSAGE, 5);
          setLoading(prev => ({ ...prev, systemModels: false }));
        })(e);
      }
    };

    resetFields(['systemModel']);
    if (!!selectedAirlineCode) {
      getSystemModels(selectedAirlineCode);
    }
    setSelectedSystemModel();
  }, [resetFields, selectedAirlineCode]);

  const isEnhancedModel = ['l3e', 'l5e'].some(enhancedModel =>
    selectedSystemModel?.displayName.toLowerCase().includes(enhancedModel)
  );

  const handleSystemModelChange = async systemModel => {
    if (!!systemModel) {
      setLoading(prev => ({ ...prev, systemModelProfile: false }));
      try {
        const data = await getSystemModelProfile(systemModel);
        setSelectedSystemModel(data);
        setLoading(prev => ({ ...prev, systemModelProfile: false }));
      } catch (e) {
        handleAuthErr(err => {
          logger.error(err);
          message.error(constants.API_ERR_MESSAGE, 5);
          setLoading(prev => ({ ...prev, systemModelProfile: false }));
        })(e);
      }
    }
  };

  const formStyle = css`
    width: 40rem;

    input[name='origin-nai'] {
      width: 16.25rem;
    }
  `;

  return (
    <Form layout="vertical" css={formStyle} hideRequiredMark>
      <p>
        No existing configuration was found for NAI <b>{nai}</b>. To proceed, please provide the
        following details...
      </p>
      <br />
      <Form.Item style={{ marginBottom: '0', paddingBottom: '0' }}>
        <Form.Item label="Division" style={{ display: 'inline-block', padding: '0 0.5rem' }}>
          {getFieldDecorator('division')(
            <Radio.Group
              size="large"
              onChange={({ target }) => setDivision(target?.value)}
              name="division-options"
            >
              <Radio.Button value={constants.BA}>{constants.BA}</Radio.Button>
              <Radio.Button value={constants.CA}>{constants.CA}</Radio.Button>
            </Radio.Group>
          )}
        </Form.Item>
        {division === constants.CA && (
          <Form.Item
            label="Airline"
            style={{ display: 'inline-block', marginLeft: '1.8rem', padding: '0 0.5rem' }}
          >
            {getFieldDecorator('airlineCode', {
              rules: [{ required: true, message: 'An airline must be selected' }],
            })(
              <Select
                filterOption={selectFilterFn}
                loading={loading.airlineCodes}
                onSelect={setSelectedAirlineCode}
                optionFilterProp="children"
                placeholder="Airline"
                showSearch
                size="large"
                style={{ width: 150 }}
              >
                {!!airlineCodes?.length &&
                  airlineCodes?.map(airlineCode => (
                    <Option key={airlineCode} value={airlineCode}>
                      {airlineCode}
                    </Option>
                  ))}
              </Select>
            )}
          </Form.Item>
        )}
        <Form.Item
          label="System Model"
          style={{ display: 'inline-block', marginLeft: '1.8rem', padding: '0 0.5rem' }}
        >
          {getFieldDecorator('systemModel', {
            rules: [{ required: true, message: 'A system model must be selected' }],
          })(
            <Select
              data-testid={`system-model-dropper-${!systemModels?.length ? 'disabled' : 'enabled'}`}
              disabled={!division || !selectedAirlineCode}
              loading={loading.systemModels}
              onSelect={handleSystemModelChange}
              placeholder="Model"
              size="large"
              style={{ width: 150 }}
            >
              {systemModels?.map &&
                systemModels.map(systemModel => (
                  <Option key={systemModel} value={systemModel}>
                    {systemModel}
                  </Option>
                ))}
            </Select>
          )}
        </Form.Item>
        {isEnhancedModel && (
          <Form.Item
            label="Origin Serial NAI"
            style={{ display: 'inline-block', marginLeft: '1.8rem', padding: '0 0.5rem' }}
          >
            {getFieldDecorator('originNai', { initialValue: '' })(
              <Input name="origin-nai" size="large" placeholder="If available..." />
            )}
          </Form.Item>
        )}
      </Form.Item>
      <Divider style={{ margin: '0.6rem 0' }} />
      <br />
      {!!selectedSystemModel?.aircards?.length ? (
        selectedSystemModel.aircards.map((aircardGroup, index) => {
          const { type, qty } = aircardGroup;
          const FormComponent = AircardForm.Provision[type.toUpperCase()];
          const aircardGroupForms = [];
          for (let i = 0; i < qty; i++) {
            aircardGroupForms.push(
              <FormComponent
                // eslint-disable-next-line react/no-array-index-key
                key={`${type}${index}-${i}`}
                getFieldDecorator={getFieldDecorator}
                type={type}
                index={i}
              />
            );
          }
          return aircardGroupForms;
        })
      ) : (
        <Skeleton paragraph={{ rows: 6 }} />
      )}
    </Form>
  );
});

const ProvisionModal = ({ nai, updateConfig, visible, close, cancel }) => {
  const [loading, setLoading] = useState(false);
  const [formRef, setFormRef] = useState(null);
  const [inErrorState, setInErrorState] = useState(false);

  // Shrug: https://medium.com/@alef.duarte/using-ant-design-form-inside-a-modal-in-react-stateless-functional-component-634f33357c80
  const saveFormRef = useCallback(node => {
    if (node !== null) setFormRef(node);
  }, []);

  const handleConfirm = () => {
    if (!!formRef?.validateFields && !!formRef?.setFields) {
      formRef.validateFields(async (errors, values) => {
        const duplicateValueErrors = checkDuplicateAircardValues(values?.aircards);
        if (!!duplicateValueErrors) {
          formRef.setFields(duplicateValueErrors);
        } else if (!errors) {
          setLoading(true);
          try {
            const newSystemConfig = buildConfig(values, nai);
            const data = await provisionSystem(newSystemConfig);
            setLoading(false);
            updateConfig(data);
            close();
          } catch (e) {
            handleAuthErr(err => {
              logger.error(err);
              setLoading(false);
              if (err instanceof BadRequestError) {
                message.error(`Bad Request: ${err.message || 'No message available'}`, 5);
              } else {
                message.error(constants.API_ERR_MESSAGE, 5);
                setInErrorState(true);
              }
            })(e);
          }
        }
      });
    }
  };

  return (
    <Modal
      confirmLoading={loading}
      onOk={handleConfirm}
      cancelButtonProps={{
        type: 'link',
        disabled: loading,
      }}
      closable={false}
      maskClosable={false}
      okButtonProps={{
        disabled: loading || inErrorState,
      }}
      okText="Provision"
      onCancel={cancel}
      style={{
        display: 'flex',
        justifyContent: 'center',
      }}
      title="Provision New System"
      visible={visible}
    >
      <Spin tip="Provisioning new system..." spinning={loading} delay={500}>
        <ProvisionForm ref={saveFormRef} nai={nai} cancel={cancel} />
      </Spin>
    </Modal>
  );
};

const selectFilterFn = (input, option) =>
  option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;

const buildConfig = (values, nai) => {
  const { aircards, airlineCode, division, systemModel, originNai } = values;

  const normalizedAircards = normalizeAircards(aircards);

  return {
    nai,
    aircards: normalizedAircards,
    airlineCode: division === constants.BA ? constants.BA : airlineCode,
    model: systemModel,
    subnetGroup: systemModel, // For initial provision, subnetGroup will always be equal to the systemModel
    originNai,
  };
};

export default ProvisionModal;
